import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Autorenew as AutorenewIcon,
  Timeline as TimelineIcon,
  ViewList as ViewListIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCache } from '../../contexts/CacheContext';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const Analizador = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Cache context
  const {
    getAnalyzerDomainsCache,
    setAnalyzerDomainsCache,
    getAnalyzerReportsCache,
    setAnalyzerReportsCache,
    getAnalyzerSubscriptionCache,
    setAnalyzerSubscriptionCache,
    isCacheExpired,
  } = useCache();

  const [loading, setLoading] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainReports, setDomainReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasAnalyserSubscription, setHasAnalyserSubscription] = useState(false);
  const [analyserService, setAnalyserService] = useState(null);
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    averageScore: 0,
    lastAnalysis: null
  });

  // Estado para indicar si se está actualizando en segundo plano
  const [isRefreshingInBackground, setIsRefreshingInBackground] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  useEffect(() => {
    fetchDomains();
    fetchAnalyserSubscription();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const fetchAnalyserSubscription = useCallback(async (forceRefresh = false) => {
    try {
      setLoadingSubscription(true);

      // Verificar caché primero
      const cachedSubscription = getAnalyzerSubscriptionCache();
      const isExpired = isCacheExpired('analyzerSubscription');

      if (!forceRefresh && cachedSubscription && cachedSubscription.data && !isExpired) {
        setHasAnalyserSubscription(cachedSubscription.data.hasSubscription);
        setAnalyserService(cachedSubscription.data.service);
        setLoadingSubscription(false);
        return;
      }

      const response = await api.get(`/servicios?user_id=${user.id}&all=true`);
      const services = response.data.data || response.data;

      // Buscar servicio de tipo Analyser activo
      const analyser = services.find(
        service => service.service_type === 'Analyser' && service.status === true
      );

      if (analyser) {
        setHasAnalyserSubscription(true);
        setAnalyserService(analyser);
        setAnalyzerSubscriptionCache(true, analyser);
      } else {
        setHasAnalyserSubscription(false);
        setAnalyserService(null);
        setAnalyzerSubscriptionCache(false, null);
      }
    } catch (error) {
      setHasAnalyserSubscription(false);
    } finally {
      setLoadingSubscription(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]); // ✅ Solo user.id, las funciones del caché ya no cambian

  const fetchDomainReports = useCallback(async (domain, forceRefresh = false) => {
    try {
      // Verificar caché primero
      const cachedReports = getAnalyzerReportsCache(domain);

      if (!forceRefresh && cachedReports) {
        setDomainReports(cachedReports.reports);
        setStats(cachedReports.stats);
        return;
      }

      const response = await api.get(`/customer-domain-path-reports/${user.id}/${domain}`);
      const reports = response.data.sanitizedCustomerDomainPathReports || [];
      setDomainReports(reports);

      // Calculate real stats from data
      let calculatedStats;
      if (reports.length > 0) {
        // Calculate average score from successful analyses
        const successfulReports = reports.filter(r => r.status === true && r.accesibility_score);
        const averageScore = successfulReports.length > 0
          ? Math.round(
              successfulReports.reduce((sum, r) => sum + parseFloat(r.accesibility_score), 0) /
              successfulReports.length
            )
          : 0;

        // Get most recent analysis date
        const sortedReports = [...reports].sort((a, b) => {
          const dateA = a.analysis_date ? new Date(a.analysis_date) : new Date(0);
          const dateB = b.analysis_date ? new Date(b.analysis_date) : new Date(0);
          return dateB - dateA;
        });

        const lastAnalysisDate = sortedReports[0]?.analysis_date
          ? new Date(sortedReports[0].analysis_date).toLocaleDateString()
          : null;

        calculatedStats = {
          totalAnalyses: reports.length,
          averageScore: averageScore,
          lastAnalysis: lastAnalysisDate
        };
      } else {
        calculatedStats = {
          totalAnalyses: 0,
          averageScore: 0,
          lastAnalysis: null
        };
      }

      setStats(calculatedStats);
      setAnalyzerReportsCache(domain, reports, calculatedStats);
    } catch (error) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]); // ✅ Solo user.id

  const fetchDomains = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Verificar caché primero
      const cachedDomains = getAnalyzerDomainsCache();
      const isExpired = isCacheExpired('analyzerDomains');

      if (!forceRefresh && cachedDomains && cachedDomains.data && !isExpired) {
        const domainList = cachedDomains.data;
        setDomains(domainList);

        if (domainList.length > 0) {
          const firstDomain = domainList[0].domain;
          setSelectedDomain(firstDomain);
          await fetchDomainReports(firstDomain);
        }
        setLoading(false);
        return;
      }

      const response = await api.get(`/customer-domain-reports/${user.id}`);
      const domainList = response.data.customerDomainReports || [];
      setDomains(domainList);
      setAnalyzerDomainsCache(domainList);

      if (domainList.length > 0) {
        const firstDomain = domainList[0].domain;
        setSelectedDomain(firstDomain);
        await fetchDomainReports(firstDomain);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, fetchDomainReports]); // ✅ Solo user.id y fetchDomainReports

  const handleDomainChange = async (domain) => {
    setSelectedDomain(domain);
    await fetchDomainReports(domain);
  };

  const handleViewReport = (analysisSeq) => {
    navigate(`/dashboard/analizador/${analysisSeq}`);
  };

  const handleNewAnalysis = () => {
    navigate('/dashboard/analizador/nuevo');
  };

  // Función para refresh manual
  const handleRefreshData = async () => {
    setIsRefreshingInBackground(true);
    setLoading(true);
    try {
      await Promise.all([
        fetchDomains(true), // forceRefresh = true
        fetchAnalyserSubscription(true), // forceRefresh = true
      ]);
      // fetchDomainReports se llama automáticamente desde fetchDomains
    } catch (error) {
    } finally {
      setIsRefreshingInBackground(false);
      setLoading(false);
    }
  };

  /* const getScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  }; */

  const getScoreIcon = (score) => {
    if (score >= 90) return <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />;
    if (score >= 70) return <WarningIcon fontSize="small" sx={{ color: '#ff9800' }} />;
    return <ErrorIcon fontSize="small" sx={{ color: '#f44336' }} />;
  };

  const filteredReports = domainReports.filter(report =>
    report.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || loadingSubscription) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100vw",
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Header fijo como en Home */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: "12px 16px", sm: "8px 16px", md: "12px 16px" },
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          backgroundColor: "white",
          zIndex: 1000,
        }}
      >
        {/* Fila principal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Columna izquierda: Texto */}
          <Box sx={{ 
            display: { xs: "flex", sm: "flex" }, 
            flexDirection: "column", 
            flex: 1,
            ml: { xs: "50px", sm: "50px", md: 35.5 },
          }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" },
                fontWeight: "medium",
                color: "#000",
              }}
            >
              {t('Analizador de Accesibilidad')}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "12px", sm: "15px", md: "14px" },
                color: "#666",
                mt: { xs: 0, md: 0.3 },
                display: { xs: "none", sm: "none", md: "none", lg: "block" },
              }}
            >
              {t('Analiza la accesibilidad de tu sitio web y obtén recomendaciones de mejora')}
            </Typography>
          </Box>

          {/* Columna derecha: Botones */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              mr: { xs: 1.5, md: 5 },
              alignItems: "center",
            }}
          >
            <Box sx={{ display: { xs: "none", sm: "none", md: "none", lg: "flex" }, gap: 0.5 }}>
              <IconButton
                onClick={handleRefreshData}
                disabled={isRefreshingInBackground}
                sx={{
                  color: "#0F52BD",
                  "&:hover": { backgroundColor: "#E3F2FD" },
                  animation: isRefreshingInBackground ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            {hasAnalyserSubscription ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard/detalles-plan-analizador', {
                    state: { service_id: analyserService.service_id }
                  })}
                  sx={{
                    textTransform: "none",
                    fontWeight: "medium",
                    color: "#0F52BD",
                    borderColor: "#0F52BD",
                    fontSize: { sm: "12px", md: "12px" },
                    px: { sm: 1.5, md: 1.5 },
                    py: { sm: 0.8, md: 0.8 },
                    borderRadius: "8px",
                    borderWidth: "2px"
                  }}
                >
                  {t('Modificar Plan')}
                </Button>
                
                {/* Nuevo Análisis - Solo ícono en móvil */}
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  <IconButton
                    onClick={handleNewAnalysis}
                    sx={{
                      color: "#0F52BD",
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "#E3F2FD" },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: "28px" }} />
                  </IconButton>
                </Box>

                {/* Nuevo Análisis - Botón completo en tablet/desktop */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon sx={{ fontSize: { sm: "18px", md: "16px" } }} />}
                    onClick={handleNewAnalysis}
                    sx={{
                      textTransform: "none",
                      fontWeight: "medium",
                      backgroundColor: "#0F52BD",
                      fontSize: { sm: "12px", md: "12px" },
                      px: { sm: 1.5, md: 1.5 },
                      py: { sm: 0.8, md: 0.8 },
                      borderRadius: "8px",
                      '&:hover': { backgroundColor: '#0a3d8f' }
                    }}
                  >
                    {t('Nuevo Análisis')}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                {/* Botón de contratar - Solo icono en móviles */}
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  <IconButton
                    onClick={() => setContactDialogOpen(true)}
                    sx={{
                      color: "#0F52BD",
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "#E3F2FD" },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: "28px" }} />
                  </IconButton>
                </Box>

                {/* Botón de contratar - Con texto en tablets/PC */}
                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                  <Button
                    variant="contained"
                    onClick={() => setContactDialogOpen(true)}
                    sx={{
                      textTransform: "none",
                      fontWeight: "medium",
                      backgroundColor: "#0F52BD",
                      fontSize: { sm: "12px", md: "12px" },
                      px: { sm: 1.5, md: 1.5 },
                      py: { sm: 0.8, md: 0.8 },
                      borderRadius: "8px",
                      '&:hover': { backgroundColor: '#0a3d8f' }
                    }}
                  >
                    {t('Contratar Analizador')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Spacer para empujar el contenido hacia abajo */}
      <Box
        sx={{
          height: { xs: "65px", sm: "70px", md: "100px", lg: "90px" },
          // Ajustar según el zoom
          "@media (max-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "65px",
          },
          "@media (max-width: 600px) and (min-resolution: 1.5dppx)": {
            height: "90px",
          },
          "@media (min-width: 600px) and (min-resolution: 1.25dppx)": {
            height: "75px",
          },
          "@media (min-width: 900px) and (min-resolution: 1.25dppx)": {
            height: "95px",
          },
        }}
      />

      {/* Contenido principal */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box
          sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}
        >
          {/* Stats Cards - Estilo como en Home */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  position: "relative",
                  height: { xs: "220px", md: "240px", lg: "280px" },
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <AutorenewIcon
                    sx={{
                      fontSize: 24,
                      mr: 1,
                      color: "#000",
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    sx={{ fontSize: "16px", color: "#000" }}
                  >
                    {t('Análisis realizados')}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "13px", color: "#666", mb: 2 }}
                >
                  {t('Análisis de accesibilidad realizados en todos tus dominios')}
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 24,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "32px",
                      color: "#4F4F4F",
                      fontWeight: "bold",
                      lineHeight: 1,
                    }}
                  >
                    {stats.totalAnalyses}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "12px", color: "#666" }}
                  >
                    {t('análisis realizado')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  position: "relative",
                  height: { xs: "220px", md: "240px", lg: "280px" },
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TimelineIcon
                    sx={{
                      fontSize: 24,
                      mr: 1,
                      color: "#000",
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    sx={{ fontSize: "16px", color: "#000" }}
                  >
                    {t('Puntuación promedio')}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "13px", color: "#666", mb: 2 }}
                >
                  {t('Puntuación media de accesibilidad de tus análisis')}
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 24,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "32px",
                      color: "#0F52BD",
                      fontWeight: "bold",
                      lineHeight: 1,
                    }}
                  >
                    {stats.averageScore}%
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "12px", color: "#666" }}
                  >
                    {t('de puntuación media')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  pt: 4,
                  pl: 4,
                  pr: 3,
                  pb: 3,
                  position: "relative",
                  height: { xs: "220px", md: "240px", lg: "280px" },
                  width: { xs: "100%", lg: "483px" },
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  boxShadow: "0px 0px 10px 2px rgba(98, 113, 137, 0.46)",
                  borderRadius: 2.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <ViewListIcon
                    sx={{
                      fontSize: 24,
                      mr: 1,
                      color: "#000",
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    sx={{ fontSize: "16px", color: "#000" }}
                  >
                    {t('Dominios analizados')}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ fontSize: "13px", color: "#666", mb: 2 }}
                >
                  {t('Sitios web únicos que has analizado')}
                </Typography>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 24,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: "32px",
                      color: "#4F4F4F",
                      fontWeight: "bold",
                      lineHeight: 1,
                    }}
                  >
                    {domains.length}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "12px", color: "#666" }}
                  >
                    {t('dominios analizados')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Filtros y búsqueda */}
          <Box mb={3} display="flex" flexDirection="column" gap={2}>
            {domains.length > 0 && (
              <TextField
                select
                label={t('Dominio')}
                value={selectedDomain || ''}
                onChange={(e) => handleDomainChange(e.target.value)}
                variant="outlined"
                size="medium"
                sx={{ width: { xs: "100%", lg: "483px" } }}
                SelectProps={{ native: true }}
              >
                {domains.map((domainObj) => (
                  <option key={domainObj.domain} value={domainObj.domain}>
                    {domainObj.domain}
                  </option>
                ))}
              </TextField>
            )}
            
            <TextField
              placeholder={t('Buscar por ruta...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", lg: "483px" } }}
            />
          </Box>

          {/* Tabla de resultados - Estilo como en Home */}
          {domains.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center',
                boxShadow: "0px 4px 10px 0px rgba(98, 113, 137, 0.46)",
                borderRadius: 2.5,
              }}
            >
              <AutorenewIcon sx={{ fontSize: 60, color: '#666', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: '#000' }}>
                {t('No hay análisis disponibles')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('Comienza analizando la accesibilidad de tu primer sitio web')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewAnalysis}
                sx={{ 
                  backgroundColor: '#0F52BD',
                  '&:hover': { backgroundColor: '#0a3d8f' },
                  textTransform: 'none',
                  fontWeight: 'medium',
                  px: 3,
                  py: 1.5,
                }}
              >
                {t('Realizar Primer Análisis')}
              </Button>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                boxShadow: "0px 4px 10px 0px rgba(98, 113, 137, 0.46)",
                borderRadius: 2.5,
                p: 0,
                overflow: 'hidden',
              }}
            >
              <Box 
                sx={{ 
                  width: '100%', 
                  overflowX: 'auto', 
                  overflowY: 'visible',
                  WebkitOverflowScrolling: 'touch',
                  p: 2,
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                  },
                }}
              >
              {/* HEADER */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 0.5fr',
                  gap: 1,
                  pb: 2,
                  mb: 2,
                  borderBottom: '2px solid #E0E0E0',
                  minWidth: { xs: '700px', md: 'auto' },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#000',
                    px: 1,
                  }}
                >
                  {t('Ruta')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#000',
                    px: 1,
                  }}
                >
                  {t('Puntuación')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#000',
                    px: 1,
                  }}
                >
                  {t('Estado')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#000',
                    px: 1,
                  }}
                >
                  {t('Último análisis')}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#000',
                    px: 1,
                    textAlign: 'center',
                  }}
                >
                  {t('Acciones')}
                </Typography>
              </Box>

              {/* CONTENIDO */}
              {filteredReports.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    py: 4,
                  }}
                >
                  <Typography color="text.secondary">
                    {searchTerm 
                      ? t('No se encontraron análisis que coincidan con la búsqueda')
                      : t('No hay análisis para este dominio')}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0}>
                  {filteredReports.map((report) => {
                    // Use real data from API
                    const score = report.accesibility_score ? parseFloat(report.accesibility_score) : null;
                    const isCompleted = report.status === true;
                    const statusCode = report.status_code || 'pending';
                    const analysisDate = report.analysis_date 
                      ? new Date(report.analysis_date).toLocaleDateString()
                      : '-';
                    
                    return (
                      <Box
                        key={report.analysis_seq}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 1fr 1fr 1fr 0.5fr',
                          minWidth: { xs: '700px', md: 'auto' },
                          gap: 1,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: '1px solid #E0E0E0',
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                        }}
                      >
                        {/* Ruta */}
                        <Box sx={{ px: 1 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'medium', 
                              fontSize: '14px',
                              color: '#000',
                            }}
                          >
                            {report.path}
                          </Typography>
                        </Box>

                        {/* Puntuación */}
                        <Box sx={{ px: 1 }}>
                          {score !== null ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              {getScoreIcon(score)}
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: '500',
                                  fontSize: '14px',
                                  color: '#000',
                                }}
                              >
                                {Math.round(score)}/100
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: '14px',
                                color: '#999',
                              }}
                            >
                              {isCompleted ? 'N/A' : '-'}
                            </Typography>
                          )}
                        </Box>

                        {/* Estado */}
                        <Box sx={{ px: 1 }}>
                          <Chip 
                            label={
                              statusCode === 'completed' ? t('Completado') :
                              statusCode === 'failed' ? t('Fallido') :
                              t('Pendiente')
                            }
                            size="small"
                            sx={{
                              backgroundColor: 
                                statusCode === 'completed' && score >= 90 ? '#e8f5e9' : 
                                statusCode === 'completed' && score >= 70 ? '#fff3e0' : 
                                statusCode === 'completed' ? '#ffebee' :
                                statusCode === 'failed' ? '#ffebee' :
                                '#f5f5f5',
                              color: 
                                statusCode === 'completed' && score >= 90 ? '#2e7d32' : 
                                statusCode === 'completed' && score >= 70 ? '#f57c00' : 
                                statusCode === 'completed' ? '#c62828' :
                                statusCode === 'failed' ? '#c62828' :
                                '#666',
                              border: `1px solid ${
                                statusCode === 'completed' && score >= 90 ? '#4caf50' : 
                                statusCode === 'completed' && score >= 70 ? '#ff9800' : 
                                statusCode === 'completed' ? '#f44336' :
                                statusCode === 'failed' ? '#f44336' :
                                '#ddd'
                              }`,
                              fontWeight: 'medium',
                              fontSize: '12px',
                            }}
                          />
                        </Box>

                        {/* Fecha */}
                        <Box sx={{ px: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '14px',
                              color: '#666',
                            }}
                          >
                            {analysisDate}
                          </Typography>
                        </Box>

                        {/* Acciones */}
                        <Box 
                          sx={{ 
                            px: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            onClick={() => handleViewReport(report.analysis_seq)}
                            size="small"
                            sx={{ 
                              color: '#999',
                              '&:hover': { color: '#0F52BD' },
                              padding: '4px',
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => {}}
                            size="small"
                            sx={{ 
                              color: '#999',
                              '&:hover': { color: '#0F52BD' },
                              padding: '4px',
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              )}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      <Dialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'medium', fontSize: '24px' }}>Contratar Analizador</DialogTitle>
        <DialogContent sx={{ padding: '25px' }}>
          <Typography sx={{ fontSize: '16px', color: '#4F4F4F' }}>
            Para contratar este servicio, por favor contacta con <strong>Inforges</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ paddingRight: '25px', paddingBottom: '25px' }}>
          <Button onClick={() => setContactDialogOpen(false)} variant="contained" sx={{ textTransform: 'none', fontSize: '16px' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analizador;